import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserCard from 'flarum/forum/components/UserCard';
import CommentPost from 'flarum/forum/components/CommentPost';
import SettingsPage from 'flarum/forum/components/SettingsPage';
import FieldSet from 'flarum/common/components/FieldSet';
import Switch from 'flarum/common/components/Switch';
import Select from 'flarum/common/components/Select';
import ItemList from 'flarum/common/utils/ItemList';
import icon from 'flarum/common/helpers/icon';

import extendUser from '../common/models/extendUser';
import { formatParentLabel, formatKid } from '../common/utils/formatKidAge';

app.initializers.add('happydigital-uinfo', () => {
  extendUser();

  // Profile hero. Each entry is its own info item, because core lays these out
  // as `.UserCard-info > li { display: inline-block }` — a single wrapper
  // element would ignore that rule and break alignment with "online"/"joined".
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;

    if (!user || !user.uinfoShowOnProfile()) return;

    const kids = user.uinfoKids();

    if (!kids || !kids.length) return;

    // With names hidden the whole family reads as one phrase ("2 ve 4 yaş
    // annesi"); with names shown each kid needs its own entry.
    if (user.uinfoDisplayMode && user.uinfoDisplayMode() === 'name_age') {
      kids.forEach((kid, index) => {
        items.add(
          'uinfo-kid' + index,
          <span className="UinfoKid">
            {icon(kid.gender === 'boy' ? 'fas fa-child' : 'fas fa-baby')} {formatKid(kid)}
          </span>,
          5 - index
        );
      });

      return;
    }

    const label = formatParentLabel(kids);

    if (!label) return;

    items.add('uinfo-kids', <span className="UinfoKid">{label}</span>, 5);
  });

  // A subtitle under the author's name, so replies can account for the kids'
  // ages.
  //
  // It is a sibling of the header rather than a child of it. Everything inside
  // `.Post-header > ul` is `display: inline` — the username and the timestamp
  // share one line — so any block placed in there breaks that line and pushes
  // the timestamp down. Sitting between the header (priority 100) and the body
  // (90) gives the subtitle its own line while leaving the header untouched.
  // The avatar occupies an absolutely positioned column of its own, so this
  // line starts in the same text column as the username above it.
  extend(CommentPost.prototype, 'contentItems', function (items) {
    const label = uinfoPostLabel(this.attrs.post);

    if (!label) return;

    items.add(
      'uinfo-kids',
      <div className="UinfoSubtitle">{label}</div>,
      95
    );
  });

  // Self-service preferences live with the rest of the user's settings.
  //
  // FieldSet wraps each child in its own <li>, and .SettingsPage fieldset > ul >
  // li is what supplies the vertical rhythm. Passing one wrapper element would
  // collapse every control into a single <li> with no spacing, so the children
  // are handed over as separate items exactly like core's own privacyItems().
  extend(SettingsPage.prototype, 'settingsItems', function (items) {
    if (!app.session.user) return;

    items.add(
      'uinfo',
      FieldSet.component(
        { className: 'Settings-uinfo', label: app.translator.trans('happydigital-uinfo.forum.settings.heading') },
        uinfoSettingsItems(this).toArray()
      )
    );
  });
});

/**
 * The subtitle text for a post, or an empty string when none should be shown.
 *
 * Shared by the two extenders below so the rendered line and the marker class
 * can never disagree about whether a subtitle exists.
 *
 * @param {Post} post
 * @returns {string}
 */
function uinfoPostLabel(post) {
  const user = post && post.user();

  if (!user || !user.uinfoShowOnPosts || !user.uinfoShowOnPosts()) return '';

  return formatParentLabel(user.uinfoKids());
}

/**
 * The individual controls of the "Child Information" fieldset.
 *
 * @param {SettingsPage} page
 * @returns {ItemList}
 */
function uinfoSettingsItems(page) {
  const items = new ItemList();
  const user = app.session.user;

  // Loading flags live on the page so a redraw does not reset them.
  page.uinfoLoading = page.uinfoLoading || {};

  const save = (key, attributes) => {
    page.uinfoLoading[key] = true;

    user
      .save(attributes)
      .catch(() => {})
      .then(() => {
        page.uinfoLoading[key] = false;
        m.redraw();
      });
  };

  items.add(
    'showOnProfile',
    Switch.component(
      {
        state: !!user.uinfoShowOnProfilePref(),
        loading: !!page.uinfoLoading.showOnProfile,
        onchange: (value) => save('showOnProfile', { uinfoShowOnProfile: value }),
      },
      app.translator.trans('happydigital-uinfo.forum.settings.show_on_profile_label')
    ),
    100
  );

  items.add(
    'showOnPosts',
    Switch.component(
      {
        state: !!user.uinfoShowOnPostsPref(),
        loading: !!page.uinfoLoading.showOnPosts,
        onchange: (value) => save('showOnPosts', { uinfoShowOnPosts: value }),
      },
      app.translator.trans('happydigital-uinfo.forum.settings.show_on_posts_label')
    ),
    90
  );

  items.add(
    'displayMode',
    <div className="Form-group">
      <label>{app.translator.trans('happydigital-uinfo.forum.settings.display_mode_label')}</label>
      {Select.component({
        options: {
          age: app.translator.trans('happydigital-uinfo.forum.settings.display_mode_age'),
          name_age: app.translator.trans('happydigital-uinfo.forum.settings.display_mode_name_age'),
        },
        value: user.uinfoDisplayMode() || 'age',
        disabled: !!page.uinfoLoading.displayMode,
        onchange: (value) => save('displayMode', { uinfoDisplayMode: value }),
      })}
      <div className="helpText">{app.translator.trans('happydigital-uinfo.forum.settings.display_mode_help')}</div>
    </div>,
    80
  );

  return items;
}
